import { LOGIN_PROD_ACTIVO, urlLogin } from '../prod/runtime/login-path';
import { useEffect, useState } from 'react';
import { apiGet } from '../lib/api';
/**
 * ProtectedRoute.tsx — Route guard component
 *
 * Usage:
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *   </Route>
 *
 *   // Admin-only section
 *   <Route element={<ProtectedRoute adminOnly />}>
 *     <Route path="/admin" element={<AdminPage />} />
 *   </Route>
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useBranchSession } from '../hooks/useBranchSession';
import { SucursalSelectorModal } from './layout/SucursalSelectorModal';
import LoadingScreen from './LoadingScreen';

/** Sin sesión: al login con regreso. Con la compuerta encendida es el documento `raiz` (navegación completa, como el 302 de prod). */
function LoginRedirect({ regreso }: { regreso: string }) {
  const url = urlLogin(regreso);
  useEffect(() => { if (LOGIN_PROD_ACTIVO) window.location.replace(url); }, [url]);
  return LOGIN_PROD_ACTIVO ? null : <Navigate to={url} replace />;
}

export interface ProtectedRouteProps {
  /** If true, user must be an admin (isAdmin === true) */
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  /**
   * If set, user must belong to this Cognito group.
   * Less strict than adminOnly — for feature-level gating.
   */
  requiredGroup?: string;
}

export function ProtectedRoute({
  adminOnly,
  superAdminOnly,
  requiredGroup,
}: ProtectedRouteProps): React.ReactElement {
  const { user, loading, isAdmin, hasGroup, error, sesionCerrada } = useAuth();
  const { isMultiBranch, branchSelected, branchList, branchId, setBranchId } = useBranchSession();
  const location = useLocation();

  const [superAdmin, setSuperAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    if (!superAdminOnly || !user) return;
    let active = true;
    setSuperAdmin(null);
    void apiGet<{ isSuperAdmin: boolean }>('/api/admin/access')
      .then(r => { if (active) setSuperAdmin(r.response?.isSuperAdmin === true); })
      .catch(() => { if (active) setSuperAdmin(false); });
    return () => { active = false; };
  }, [superAdminOnly, user?.id]);

  // Still checking session
  if (loading) {
    return <LoadingScreen />;
  }

  // Logout (de esta pestaña o de otra): sesion-salida ya navega al login sin regreso, como LogOff de prod
  // (AccountController.cs:939-942). Un segundo envío con ReturnUrl cancelaría esa navegación.
  if (!user && sesionCerrada) return <LoadingScreen />;

  // Not authenticated → redirect to login with the return URL (prod: LoginPath = /inicio con ReturnUrl)
  if (!user) {
    const returnTo = location.pathname + location.search;
    return <LoginRedirect regreso={returnTo} />;
  }

  if (branchList.length === 0) {
    return <p role="alert">Tu usuario no tiene sucursales activas. Pide a tu administrador que te dé acceso.</p>;
  }
  if (superAdminOnly && superAdmin === null) return <LoadingScreen />;
  if (superAdminOnly && !superAdmin) return <Navigate to="/" replace />;

  // Admin-only check
  if (adminOnly && !isAdmin()) {
    return <Navigate to="/404" replace />;
  }

  // Group check
  if (requiredGroup && !hasGroup(requiredGroup) && !isAdmin()) {
    return <Navigate to="/404" replace />;
  }

  // Multi-sucursal (paridad con prod): el usuario debe elegir a qué sucursal
  // entrar antes de operar. Con una sola sucursal se autoselecciona (no se ve).
  if (isMultiBranch && !branchSelected) {
    return (
      <>
      {error && <p role="alert">{error}</p>}
      <SucursalSelectorModal
        open
        mandatory
        branches={branchList}
        currentId={branchId}
        onSelect={setBranchId}
      />
      </>
    );
  }

  return <Outlet />;
}
